import { requestEmailOtp } from "@src/appV2/Auth/cognito/emailOtpAuth";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  logAuthRedirectFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";
import { APP_V3_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";

interface SendEmailOtpParams {
  email: string;
  /**
   * Skip the `ensureCognitoUser` JIT-import step on a resend, mirroring the
   * magic link flow (DEVOP-5526). The first send imports legacy users.
   */
  skipEnsureUser?: boolean;
  /**
   * Cognito username resolved by a previous send, passed on a resend so it
   * reuses that username instead of the (possibly-inactive) email alias.
   */
  username?: string;
  /**
   * When set, prefer the password prompt over an email code if the account
   * advertises a usable password (redesigned workplace login). Resolves to
   * `requiresPassword` without sending a code.
   */
  preferPasswordIfUsable?: boolean;
}

export type SendEmailOtpResult =
  | { success: true; username: string }
  | { success: false; error?: string; requiresPassword?: boolean };

/**
 * Requests a Cognito email OTP login code.
 *
 * JIT-imports legacy workplace users via `ensureCognitoUser`, then starts the
 * native Cognito EMAIL_OTP sign-in flow, which emails an 8-digit code (no link).
 * The code is bound to this browser's sign-in session, so it must be entered
 * here — there is no cross-device redemption.
 */
export async function sendEmailOtp({
  email,
  skipEnsureUser = false,
  username: providedUsername,
  preferPasswordIfUsable = false,
}: SendEmailOtpParams): Promise<SendEmailOtpResult> {
  setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_EMAIL_OTP);

  try {
    const { username, prefersPassword } = await requestEmailOtp({
      email,
      skipEnsureUser,
      username: providedUsername,
      preferPasswordIfUsable,
    });
    if (prefersPassword) {
      // Account has a usable password; the redesigned flow prefers it over an
      // email code. No code was sent — route to the password prompt (no error).
      // Log on the same funnel as the other outcomes so rollout adoption (how
      // often the preference fires) is measurable.
      logEvent(APP_V3_APP_EVENTS.EMAIL_OTP_REQUESTED, {
        outcome: LOGIN_OUTCOME.EXPECTED_ERROR,
        reason: LOGIN_REASON.PASSWORD_PREFERRED,
      });
      return { success: false, requiresPassword: true };
    }
    logEvent(APP_V3_APP_EVENTS.EMAIL_OTP_REQUESTED, { outcome: LOGIN_OUTCOME.SUCCESS });
    return { success: true, username };
  } catch (error: unknown) {
    const mappedError = mapCognitoError(error, "magic-link-request");
    const { outcome, reason } =
      mappedError.code === "auth/unknown" && isNetworkFailure(error)
        ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
        : classifyCognitoError(mappedError.code);
    logAuthRedirectFailure({
      error,
      metadata: {
        errorCode: mappedError.code,
        errorMessage: mappedError.message,
        email,
        outcome,
        reason,
      },
    });
    // EMAIL_OTP isn't available for this account — Cognito only offers password.
    // Tell the caller to route the user to password sign-in instead of showing a
    // dead-end error on the email step.
    if (mappedError.code === "auth/email-otp-unavailable") {
      return { success: false, error: mappedError.message, requiresPassword: true };
    }
    return { success: false, error: mappedError.message };
  }
}
